import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PageTransition } from '@/components/ui/page-transition';
import { routes, notFoundRoute } from '@/config/routes';
import { DashboardSkeleton } from '@/components/ui/skeleton';
import { PageGate } from '@/components/access/PageGate';
import { LazyRouteWrapper } from './LazyRouteWrapper';
import { useModuleVisitTracker } from '@/hooks/useModuleVisitTracker';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useEngagementTracking } from '@/hooks/useEngagementTracking';

// Shimmer skeleton fallback for route transitions
const RouteLoader = () => <DashboardSkeleton />;

const Auth = lazy(() => import('@/pages/Auth'));

// Map route paths to module IDs for visibility
const getModuleId = (path: string): string => {
  return path.replace(/^\//, '').replace(/-/g, '-') || 'dashboard';
};

export function AnimatedRoutes() {
  // Track module visits globally for Observatory Mastery progress
  useModuleVisitTracker();
  
  // Combined engagement tracking (scroll depth + time on page + interactions)
  useEngagementTracking();
  
  const location = useLocation();
  const { trackPageView } = useAnalytics();
  
  // Track page views on route changes
  useEffect(() => {
    const route = routes.find(r => r.path === location.pathname);
    const pageName = route?.name || location.pathname.replace(/^\//, '') || 'Home';
    trackPageView(pageName);
  }, [location.pathname, trackPageView]);
  
  return (
    <Suspense fallback={<RouteLoader />}>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        
        <Route
          path="/auth"
          element={
            <PageTransition>
              <LazyRouteWrapper 
                lazyComponent={Auth} 
                fallback={<RouteLoader />}
                routeName="Auth"
              />
            </PageTransition>
          }
        />
        
        {routes.map((route) => {
          const moduleId = getModuleId(route.path);
          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                <PageTransition>
                  <PageGate moduleId={moduleId}>
                    <LazyRouteWrapper 
                      lazyComponent={route.component} 
                      fallback={<RouteLoader />}
                      routeName={route.name}
                    />
                  </PageGate>
                </PageTransition>
              }
            />
          );
        })}
        
        <Route
          path={notFoundRoute.path}
          element={
            <PageTransition>
              <LazyRouteWrapper 
                lazyComponent={notFoundRoute.component} 
                fallback={<RouteLoader />}
                routeName="NotFound"
              />
            </PageTransition>
          }
        />
      </Routes>
    </Suspense>
  );
}
